"use client";
import { twMerge } from "tailwind-merge";
import type { LinkComponentType } from "../../index";
import { formatPhoneNumber } from "../../utils/formatting/phone";
import { Avatar } from "../Avatar";
import { Card } from "../Card";
import { Icon } from "../Icon";
import { StaticMap } from "../Maps/StaticMap";
import { Skeleton } from "../Skeleton";
import { TextLink } from "../TextLink";

export interface SiteContactCardProps {
  /** Site information */
  site: {
    streetAddress: string;
    city?: string;
    state?: string;
    postalCode?: string;
    longitude?: number;
    latitude?: number;
    href?: string;
    /** Entity identifier for PII masking (e.g., "site:site_abc123") */
    piiEntity?: string;
  };

  /** Contact information (optional) */
  contact?: {
    firstName: string;
    lastName: string;
    email?: string;
    phone?: string;
    avatarUrl?: string;
    href?: string;
    /** Entity identifier for PII masking (e.g., "contact:ctc_abc123") */
    piiEntity?: string;
  };

  /** Custom link component (e.g., Next.js Link) */
  LinkComponent?: LinkComponentType;

  /** Card variant */
  variant?: "outlined" | "elevated" | "filled" | "ghost";

  /** Layout mode
   * @default "stacked"
   * - "stacked": Traditional stacked layout (map on top, content below)
   * - "row": Full-width row layout on desktop (address left, map right), stacked on mobile
   */
  layout?: "stacked" | "row";

  /** Show map (requires site longitude/latitude) */
  showMap?: boolean;

  /** Map height */
  mapHeight?: number | string;

  /** Mapbox access token (required for map display) */
  mapboxAccessToken?: string;

  /** Show contact email
   * @default true
   */
  showEmail?: boolean;

  /** Show contact phone
   * @default false
   */
  showPhone?: boolean;

  /** Loading state */
  isLoading?: boolean;

  /** Additional classes for the card */
  className?: string;

  /** Additional classes for the card content (for padding control, etc.) */
  contentClassName?: string;

  /** Whether the card should fill the full height of its container */
  fullHeight?: boolean;

  /** Show expand button on map to open interactive fullscreen view
   * @default false
   */
  showMapExpandToggle?: boolean;

  /** Title for the expanded map dialog
   * @default "Explore map"
   */
  expandedMapTitle?: string;

  /** Address label to show in expanded map view (defaults to formatted site address) */
  expandedMapAddressLabel?: string;
}

/**
 * SiteContactCard
 *
 * A combined card component for displaying both site and contact information.
 * Shows site details with optional map, followed by associated contact information.
 *
 * @example
 * ```tsx
 * <SiteContactCard
 *   site={{
 *     streetAddress: "123 Main St",
 *     city: "San Francisco",
 *     state: "CA",
 *     postalCode: "94102",
 *     longitude: -122.4194,
 *     latitude: 37.7749,
 *     href: "/sites/123"
 *   }}
 *   contact={{
 *     firstName: "John",
 *     lastName: "Doe",
 *     email: "john.doe@example.com",
 *     phone: "+1 (555) 123-4567",
 *     href: "/contacts/123"
 *   }}
 *   showMap
 * />
 * ```
 */
export function SiteContactCard({
  site,
  contact,
  LinkComponent,
  variant = "outlined",
  layout = "stacked",
  showMap = true,
  mapHeight = 200,
  mapboxAccessToken,
  showEmail = true,
  showPhone = false,
  isLoading = false,
  className,
  contentClassName,
  fullHeight = false,
  showMapExpandToggle = false,
  expandedMapTitle = "Explore map",
  expandedMapAddressLabel,
}: SiteContactCardProps) {
  // Build city/state/zip line for site
  const locationParts: string[] = [];
  if (site.city) locationParts.push(site.city);
  if (site.state && site.postalCode) {
    locationParts.push(`${site.state} ${site.postalCode}`);
  } else if (site.state) {
    locationParts.push(site.state);
  } else if (site.postalCode) {
    locationParts.push(site.postalCode);
  }
  const locationLine = locationParts.join(", ");

  // Build full address for expanded map label
  const fullAddress = expandedMapAddressLabel || [site.streetAddress, locationLine].filter(Boolean).join(", ");

  // Determine if we can show the map
  const canShowMap = showMap && site.longitude !== undefined && site.latitude !== undefined;

  // Contact full name
  const contactFullName = contact ? `${contact.firstName} ${contact.lastName}`.trim() : "";

  // Link wrapper component
  const LinkWrapper = ({
    children,
    href,
    className: linkClassName,
  }: {
    children: React.ReactNode;
    href?: string;
    className?: string;
  }) => {
    if (!href) {
      return <span className={linkClassName}>{children}</span>;
    }

    if (LinkComponent) {
      return (
        <LinkComponent href={href} className={linkClassName}>
          {children}
        </LinkComponent>
      );
    }

    return (
      <TextLink href={href} variant="unstyled" className={twMerge("hover:underline", linkClassName)} asButton={false}>
        {children}
      </TextLink>
    );
  };

  if (isLoading) {
    const isRowLayout = layout === "row";

    return (
      <Card
        variant={variant}
        className={twMerge(
          fullHeight && "h-full",
          isRowLayout ? "flex flex-col md:flex-row" : "flex flex-col",
          className
        )}
      >
        {canShowMap && (
          <Card.Media
            aspectRatio="auto"
            className={twMerge(isRowLayout && "md:order-2 md:flex-[2] md:min-w-0", fullHeight && "flex-1")}
          >
            <Skeleton width="100%" height={fullHeight ? "100%" : mapHeight} />
          </Card.Media>
        )}
        <Card.Content
          className={twMerge(
            "space-y-4 px-4 py-4",
            isRowLayout && "md:order-1 md:flex-[1] md:flex-shrink-0 md:flex md:flex-col md:justify-center"
          )}
        >
          <div className="space-y-2">
            <Skeleton width="100%" height={16} />
            <Skeleton width="80%" height={16} />
          </div>
          {contact && (
            <>
              <div className="border-t border-border-default" />
              <div className="flex items-center gap-2">
                <Skeleton width={40} height={40} className="rounded flex-shrink-0" />
                <div className="flex-1 space-y-1">
                  <Skeleton width="50%" height={14} />
                  <Skeleton width="100%" height={12} />
                </div>
              </div>
            </>
          )}
        </Card.Content>
      </Card>
    );
  }

  const isRowLayout = layout === "row";

  return (
    <Card
      variant={variant}
      className={twMerge(
        "group/card relative",
        site.href && "cursor-pointer",
        fullHeight && "h-full",
        isRowLayout ? "flex flex-col md:flex-row" : "flex flex-col",
        className
      )}
    >
      {site.href && (
        <LinkWrapper href={site.href} className="after:absolute after:inset-0 after:z-10">
          <span className="sr-only">{site.streetAddress}</span>
        </LinkWrapper>
      )}
      {canShowMap && (
        <Card.Media
          aspectRatio="auto"
          className={twMerge(isRowLayout && "md:order-2 md:flex-[2] md:min-w-0", fullHeight && "flex-1")}
        >
          <StaticMap
            height={fullHeight ? "100%" : mapHeight}
            initialViewState={{
              longitude: site.longitude,
              latitude: site.latitude,
              zoom: 14,
            }}
            showMarker
            mapboxAccessToken={mapboxAccessToken}
            showExpandToggle={showMapExpandToggle}
            expandedMapTitle={expandedMapTitle}
            expandedMapAddressLabel={fullAddress}
          />
        </Card.Media>
      )}
      <Card.Content
        className={twMerge(
          "space-y-4 px-4 py-4",
          isRowLayout && "md:order-1 md:flex-[1] md:flex-shrink-0 md:flex md:flex-col md:justify-center",
          contentClassName
        )}
      >
        {/* Site Section */}
        <div className="flex flex-col gap-1 min-w-0 text-left">
          <span
            className="text-xl font-semibold leading-tight text-text-heading group-hover/card:text-text-brand transition-colors"
            data-pii-type={site.piiEntity ? "address" : undefined}
            data-pii-entity={site.piiEntity}
          >
            {site.streetAddress}
          </span>
          {locationLine && (
            <span
              className="text-lg font-normal leading-relaxed text-text-muted"
              data-pii-type={site.piiEntity ? "city-state-zip" : undefined}
              data-pii-entity={site.piiEntity}
            >
              {locationLine}
            </span>
          )}
        </div>

        {/* Contact Section */}
        {contact && (
          <>
            <div className="border-t border-border-default" />
            <div className="flex items-center gap-2 relative">
              <Avatar
                firstName={contact.firstName}
                lastName={contact.lastName}
                src={contact.avatarUrl}
                size="md"
                variant="user"
                shape="square"
                className="flex-shrink-0"
                piiEntity={contact.piiEntity}
              />
              <div className="flex-1 min-w-0">
                {contact.href ? (
                  <LinkWrapper
                    href={contact.href}
                    className="text-sm font-semibold leading-tight text-text-heading block mb-0.5 hover:text-text-brand transition-colors relative z-20"
                  >
                    <span
                      data-pii-type={contact.piiEntity ? "full-name" : undefined}
                      data-pii-entity={contact.piiEntity}
                    >
                      {contactFullName}
                    </span>
                  </LinkWrapper>
                ) : (
                  <span
                    className="text-sm font-semibold leading-tight text-text-heading block mb-0.5"
                    data-pii-type={contact.piiEntity ? "full-name" : undefined}
                    data-pii-entity={contact.piiEntity}
                  >
                    {contactFullName}
                  </span>
                )}
                <div className="space-y-0.5">
                  {showEmail && contact.email && (
                    <div className="flex items-center gap-1.5 text-text-body">
                      <Icon name="Envelope" size={12} className="text-text-muted flex-shrink-0" />
                      <TextLink
                        href={`mailto:${contact.email}`}
                        variant="unstyled"
                        className="text-xs leading-tight truncate text-text-body relative z-20"
                        data-pii-type={contact.piiEntity ? "email" : undefined}
                        data-pii-entity={contact.piiEntity}
                      >
                        {contact.email}
                      </TextLink>
                    </div>
                  )}
                  {showPhone && contact.phone && (
                    <div className="flex items-center gap-1.5 text-text-body">
                      <Icon name="Phone" size={12} className="text-text-muted flex-shrink-0" />
                      <TextLink
                        href={`tel:${contact.phone}`}
                        variant="unstyled"
                        className="text-xs leading-tight truncate text-text-body relative z-20"
                        data-pii-type={contact.piiEntity ? "phone" : undefined}
                        data-pii-entity={contact.piiEntity}
                      >
                        {formatPhoneNumber(contact.phone)}
                      </TextLink>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </>
        )}
      </Card.Content>
    </Card>
  );
}
